import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    DEFAULT_LANGUAGE,
    LANGUAGE_OPTIONS,
    LANGUAGE_STORAGE_KEY,
    SUPPORTED_LANGUAGES,
    translations,
} from './translations';

const LanguageContext = createContext({
    language: DEFAULT_LANGUAGE,
    setLanguage: () => { },
    t: (key) => key,
    languageOptions: LANGUAGE_OPTIONS,
});

const normalizeLanguage = (value) => {
    if (!value || typeof value !== 'string') {
        return DEFAULT_LANGUAGE;
    }

    const base = value.toLowerCase().split('-')[0];
    return SUPPORTED_LANGUAGES.includes(base) ? base : DEFAULT_LANGUAGE;
};

const getByPath = (object, path) =>
    path.split('.').reduce((acc, current) => (acc && acc[current] !== undefined ? acc[current] : undefined), object);

const interpolate = (template, params = {}) =>
    template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) =>
        params[key] !== undefined && params[key] !== null ? String(params[key]) : '',
    );

const resolveInitialLanguage = () => {
    if (typeof window === 'undefined') {
        return DEFAULT_LANGUAGE;
    }

    const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage) {
        return normalizeLanguage(savedLanguage);
    }

    return DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguageState] = useState(resolveInitialLanguage);

    const setLanguage = useCallback((nextLanguage) => {
        setLanguageState(normalizeLanguage(nextLanguage));
    }, []);

    const t = useCallback(
        (key, params) => {
            const activeLanguagePack = translations[language] || translations[DEFAULT_LANGUAGE];
            const fallbackLanguagePack = translations[DEFAULT_LANGUAGE];

            const rawValue = getByPath(activeLanguagePack, key) ?? getByPath(fallbackLanguagePack, key) ?? key;
            if (typeof rawValue !== 'string') {
                return key;
            }

            return interpolate(rawValue, params);
        },
        [language],
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
        document.documentElement.lang = language;
    }, [language]);

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t,
            languageOptions: LANGUAGE_OPTIONS,
        }),
        [language, setLanguage, t],
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
